import React from 'react';
import type { ThemeClasses } from '@/ui/theme/ThemeContext';
import { 
  Calendar, 
  Tag, 
  User, 
  Briefcase, 
  FileText, 
  Flag, 
  CheckCircle2, 
  Layers3, 
  CheckSquare 
} from 'lucide-react';
import { ProjectNode, ChecklistItem, NodeType } from '@/shared/types/projectData';
import { cn } from '@/ui/theme/ThemeContext';
import { getNodeAssigneeDisplayNames } from '@/shared/utils/projectDataUtils';
import { AutoCompleteInput } from '@/ui/primitives/inputs/AutoCompleteInput';
import { ChecklistComponent } from '@/ui/primitives/ChecklistComponent';
import { MultiAssigneeInput } from '@/ui/primitives/inputs/MultiAssigneeInput';
import { MentionTextarea } from '@/ui/primitives/inputs/MentionTextarea';
import type { TaggableUser } from '@/features/projects/hooks/useAssigneeDirectory';
import { StatusButtonGroup, PriorityButtonGroup } from '@/ui/primitives/ButtonGroup';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { parseDateValuePreservingLocalDay } from '@/shared/utils/dates';
import { changeScheduleDay, scheduleDateInputValue } from './scheduleDateInput';

/* ============================================================================
 * TYPE DEFINITIONS
 * ============================================================================ */

export interface TypeSelectorProps {
  itemType: string;
  availableTypes: NodeType[];
  onTypeChange: (type: NodeType) => void;
  theme: ThemeClasses;
}

export interface DescriptionSectionProps {
  description: string;
  onDescriptionChange: (value: string, mentionIds: string[]) => void;
  mentionUsers: TaggableUser[];
  readOnly?: boolean;
  theme: ThemeClasses;
}

export interface DatesSectionProps {
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  startTime?: string;
  endTime?: string;
  date?: string;
  itemType: string;
  onFieldChange: (field: keyof ProjectNode, value: string) => void;
  readOnly?: boolean;
  theme: ThemeClasses;
}

export interface StatusPrioritySectionProps {
  statusReadOnly?: boolean;
  status?: string;
  priority?: string;
  itemType: string;
  onFieldChange: (field: keyof ProjectNode, value: string) => void;
  readOnly?: boolean;
  theme: ThemeClasses;
}

export interface AssigneeClientSectionProps {
  assignees: string[];
  assigneeLabels?: string[];
  projectClient?: string; // Read-only project client
  onAssigneesChange: (assignees: string[]) => void;
  assigneeSuggestions: string[];
  assigneeSuggestionGroups?: Array<{ label: string; suggestions: string[] }>;
  assigneeLabelResolver?: (assignee: string) => string;
  readOnly?: boolean;
  theme: ThemeClasses;
}

export interface ProjectSectionProps {
  projectId?: string;
  projectOptions: Array<{ projectId: string; projectName: string }>;
  onProjectChange: (projectId: string) => void;
  disabled?: boolean;
  theme: ThemeClasses;
}

export interface TagsSectionProps {
  tags?: string[];
  onTagsChange: (value: string) => void;
  suggestions: string[];
  readOnly?: boolean;
  theme: ThemeClasses;
}

export interface TaggedPeopleSectionProps {
  mentions: string[];
  mentionLabels?: string[];
  readOnly?: boolean;
  theme: ThemeClasses;
}

export interface ChecklistSectionProps {
  checklist: ChecklistItem[];
  onChecklistChange: (checklist: ChecklistItem[]) => void;
  readOnly?: boolean;
  theme: ThemeClasses;
}

/* ============================================================================
 * TYPE SELECTOR COMPONENT
 * ============================================================================ */

export const TypeSelector: React.FC<TypeSelectorProps> = ({ itemType, availableTypes, onTypeChange, theme }) => {
  const { t } = useSettings();
  const typeLabels: Record<NodeType, string> = {
    task: 'Task',
    group: 'Group',
    milestone: 'Milestone',
    document: 'Note',
  };

  return (
    <div className="pm-task-type-options flex gap-2">
      {availableTypes.map(type => (
        <button
          key={type}
          type="button"
          data-testid={`task-details-type-option-${type}`}
          onClick={() => onTypeChange(type)}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-[200ms] ease-in-out",
            itemType === type 
              ? cn(theme.controlActive, theme.selectionRing)
              : cn(theme.bgSecondary, theme.surfaceHover)
          )}
        >
          {type === 'task' && <CheckCircle2 size={16} />}
          {type === 'group' && <Layers3 size={16} />}
          {type === 'milestone' && <Flag size={16} />}
          {type === 'document' && <FileText size={16} />}
          <span>{t(typeLabels[type])}</span>
        </button>
      ))}
    </div>
  );
};

/* ============================================================================
 * DESCRIPTION SECTION COMPONENT
 * ============================================================================ */

export const DescriptionSection: React.FC<DescriptionSectionProps> = ({ 
  description, 
  onDescriptionChange, 
  mentionUsers,
  readOnly = false,
  theme 
}) => {
  const { t } = useSettings();
  const descriptionId = React.useId();
  return (
    <div className="space-y-2">
      <label htmlFor={descriptionId} className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
        <FileText size={16} />
        {t('Description')}
      </label>
      <MentionTextarea
        id={descriptionId}
        value={description || ''}
        onChange={onDescriptionChange}
        candidates={mentionUsers}
        rows={3}
        readOnly={readOnly}
        placeholder={t('Add a description. Type @ to mention someone.')}
      />
    </div>
  );
};

/* ============================================================================
 * DATES SECTION COMPONENT
 * ============================================================================ */

export const DatesSection: React.FC<DatesSectionProps> = ({
  startDate,
  endDate,
  dueDate,
  startTime,
  endTime,
  date,
  itemType,
  onFieldChange,
  readOnly = false,
  theme
}) => {
  const { locale, t } = useSettings();
  const effectiveEndDate = endDate || dueDate;
  if (itemType === 'milestone') {
    return (
      <div className="space-y-2">
        <label className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
          <Calendar size={16} />
          {t('Milestone Date')}
        </label>
        {readOnly ? (
          <div className={cn("w-full px-3 py-2 rounded-lg border text-sm", theme.bgSecondary, theme.borderPrimary, theme.textPrimary)}>
            {date ? parseDateValuePreservingLocalDay(date).toLocaleDateString(locale) : t('No date')}
          </div>
        ) : (
          <input 
            type="date" 
            value={scheduleDateInputValue(date)}
            onChange={(e) => onFieldChange('date', changeScheduleDay(date, e.target.value))}
            aria-label={t('Milestone Date')}
            className={cn(
              "w-full px-3 py-2 rounded-lg border",
              theme.bgSecondary,
              theme.borderPrimary,
              theme.focusRing
            )} 
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="pm-task-date-fields grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
            <Calendar size={16} />
            {t('Start Date')}
          </label>
          {readOnly ? (
            <div className={cn("w-full px-3 py-2 rounded-lg border text-sm", theme.bgSecondary, theme.borderPrimary, theme.textPrimary)}>
              {startDate ? parseDateValuePreservingLocalDay(startDate).toLocaleDateString(locale) : t('Not set')}
            </div>
          ) : (
            <input 
              type="date" 
              value={scheduleDateInputValue(startDate)}
              onChange={(e) => {
                onFieldChange('startDate', changeScheduleDay(startDate, e.target.value, startTime));
              }} 
              aria-label={t('Start Date')}
              className={cn(
                "w-full px-3 py-2 rounded-lg border",
                theme.bgSecondary,
                theme.borderPrimary,
                theme.focusRing
              )} 
            />
          )}
        </div>
        <div className="space-y-2">
          <label className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
            <Calendar size={16} />
            {t('End Date')}
          </label>
          {readOnly ? (
            <div className={cn("w-full px-3 py-2 rounded-lg border text-sm", theme.bgSecondary, theme.borderPrimary, theme.textPrimary)}>
              {effectiveEndDate ? parseDateValuePreservingLocalDay(effectiveEndDate).toLocaleDateString(locale) : t('Not set')}
            </div>
          ) : (
            <input 
              type="date" 
              value={scheduleDateInputValue(effectiveEndDate)}
              onChange={(e) => {
                onFieldChange('endDate', changeScheduleDay(effectiveEndDate, e.target.value, endTime));
              }} 
              aria-label={t('End Date')}
              className={cn(
                "w-full px-3 py-2 rounded-lg border",
                theme.bgSecondary,
                theme.borderPrimary,
                theme.focusRing
              )} 
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
 * STATUS & PRIORITY SECTION COMPONENT
 * ============================================================================ */

export const StatusPrioritySection: React.FC<StatusPrioritySectionProps> = ({
  statusReadOnly = false,
  status,
  priority,
  itemType,
  onFieldChange,
  readOnly = false
  // theme // Not used in this component
}) => {
  const { t } = useSettings();
  if (readOnly) {
    return (
      <div className={cn("pm-task-status-priority grid gap-4", (itemType === 'task' || itemType === 'group') ? "grid-cols-2" : "grid-cols-1")}>
        {(itemType === 'task' || itemType === 'group') && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-slate-600 dark:text-gray-400">{t('Status')}</div>
            <div className="rounded-lg border px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-700">
              {status ? t(status) : t('Not set')}
            </div>
          </div>
        )}
        <div className="space-y-2">
          <div className="text-sm font-medium text-slate-600 dark:text-gray-400">{t('Priority')}</div>
          <div className="rounded-lg border px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-700">
            {priority ? t(priority) : t('Not set')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("pm-task-status-priority grid gap-4", (itemType === 'task' || itemType === 'group') ? "grid-cols-2" : "grid-cols-1")}>
      {(itemType === 'task' || itemType === 'group') && (
        statusReadOnly ? (
          <div className="space-y-2">
            <div className="text-sm font-medium text-slate-600 dark:text-gray-400">{t('Status')}</div>
            <div className="text-sm">{status ? t(status) : t('Not set')}</div>
          </div>
        ) : <StatusButtonGroup
          value={status}
          onChange={(status) => onFieldChange('status', status)}
        />
      )}
      <div className={cn(!(itemType === 'task' || itemType === 'group') && "col-span-2")}>
        <PriorityButtonGroup
          value={priority}
          onChange={(priority) => onFieldChange('priority', priority)}
        />
      </div>
    </div>
  );
};

/* ============================================================================
 * PROJECT SECTION COMPONENT
 * ============================================================================
 */

export const ProjectSection: React.FC<ProjectSectionProps> = ({
  projectId,
  projectOptions,
  onProjectChange,
  disabled = false,
  theme,
}) => {
  const { t } = useSettings();
  if (projectOptions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <label className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
        <Briefcase size={16} />
        {t('Project')}
      </label>
      <select
        value={projectId || ''}
        onChange={(e) => onProjectChange(e.target.value)}
        disabled={disabled}
        className={cn(
          "w-full px-3 py-2 rounded-lg border text-sm transition-colors",
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textPrimary,
          theme.focusRing,
          disabled && "cursor-not-allowed opacity-60"
        )}
      >
        <option value="" disabled>
          {t('Select a project')}
        </option>
        {projectOptions.map((project) => (
          <option key={project.projectId} value={project.projectId}>
            {project.projectName}
          </option>
        ))}
      </select>
    </div>
  );
};

/* ============================================================================
 * ASSIGNEE & CLIENT SECTION COMPONENT
 * ============================================================================ */

export const AssigneeClientSection: React.FC<AssigneeClientSectionProps> = ({
  assignees,
  assigneeLabels,
  projectClient,
  onAssigneesChange,
  assigneeSuggestions,
  assigneeSuggestionGroups,
  assigneeLabelResolver,
  readOnly = false,
  theme
}) => {
  const { t } = useSettings();
  const assigneeInputId = React.useId();
  return (
    <div className="pm-task-assignee-fields grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <label htmlFor={assigneeInputId} className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
          <User size={16} />
          {t('Assignee')}
        </label>
        {readOnly ? (
          <div className={cn(
            "min-h-[42px] px-3 py-2 rounded-lg border text-sm",
            theme.bgSecondary,
            theme.borderPrimary,
            theme.textPrimary
          )}>
            {(() => {
              const resolvedLabels = Array.isArray(assigneeLabels) && assigneeLabels.length > 0
                ? assigneeLabels
                : getNodeAssigneeDisplayNames(
                    { assignee: assignees[0], assignees },
                    assigneeLabelResolver
                  );
              return resolvedLabels.length > 0 ? resolvedLabels.join(', ') : t('No assignees');
            })()}
          </div>
        ) : (
          <MultiAssigneeInput
            id={assigneeInputId}
            assignees={assignees}
            onChange={(nextAssignees) => onAssigneesChange(nextAssignees.slice(-1))}
            suggestions={assigneeSuggestions}
            suggestionGroups={assigneeSuggestionGroups}
            labelForAssignee={assigneeLabelResolver}
            className="rounded-lg"
            allowCustomValues={false}
            emptySuggestionsMessage={t('No assignable people or agents')}
          />
        )}
      </div>
      <div className="space-y-2">
        <label className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
          <Briefcase size={16} />
          {t('Client')}
        </label>
        <div className={cn(
          "px-3 py-2 rounded-lg border text-sm",
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textMuted
        )}>
          {projectClient || t('No client assigned')}
        </div>
      </div>
    </div>
  );
};

export const TaggedPeopleSection: React.FC<TaggedPeopleSectionProps> = ({
  mentions: _mentions,
  mentionLabels,
  readOnly = false,
  theme
}) => {
  const { t } = useSettings();
  return (
    <div className="space-y-2">
        <label className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
          <User size={16} />
        {t('Mentioned project members')}
      </label>
      {readOnly ? (
        <div className={cn(
          "min-h-[42px] px-3 py-2 rounded-lg border text-sm",
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textPrimary
        )}>
          {mentionLabels && mentionLabels.length > 0 ? mentionLabels.join(', ') : t('No project members mentioned')}
        </div>
      ) : (
        <div className={cn(
          "min-h-[42px] rounded-lg border px-3 py-2 text-sm",
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textPrimary
        )}>
          {mentionLabels && mentionLabels.length > 0 ? mentionLabels.join(', ') : t('No project members mentioned yet')}
          <div className={cn("mt-1 text-xs", theme.textMuted)}>
            {t('Type')} <span className="font-medium">@</span> {t("in the description or docs editor to mention people from this project's member list.")}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * TAGS SECTION COMPONENT
 * ============================================================================ */

export const TagsSection: React.FC<TagsSectionProps> = ({
  tags,
  onTagsChange,
  suggestions,
  readOnly = false,
  theme
}) => {
  const { t } = useSettings();
  const tagsInputId = React.useId();
  return (
    <div className="space-y-2">
      <label htmlFor={tagsInputId} className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
          <Tag size={16} />
        {t('Tags')}
      </label>
      {readOnly ? (
        <div className={cn(
          "min-h-[42px] w-full px-3 py-2 rounded-lg border text-sm",
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textPrimary
        )}>
          {tags && tags.length > 0 ? tags.join(', ') : t('No tags')}
        </div>
      ) : (
        <AutoCompleteInput
          id={tagsInputId}
          value={tags?.join(', ') || ''}
          onChange={onTagsChange}
          placeholder={t('Enter tags separated by commas')}
          suggestions={suggestions}
          className={cn(
            "rounded-lg",
            theme.bgSecondary,
            theme.borderPrimary,
            theme.focusRing
          )}
        />
      )}
    </div>
  );
};

/* ============================================================================
 * CHECKLIST SECTION COMPONENT
 * ============================================================================ */

export const ChecklistSection: React.FC<ChecklistSectionProps> = ({
  checklist,
  onChecklistChange,
  readOnly = false,
  theme
}) => {
  const { t } = useSettings();
  if (readOnly) {
    return (
      <div className="space-y-2">
        <label className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
          <CheckSquare size={16} />
          {t('Checklist')}
        </label>
        <div className={cn("border rounded-lg p-3 space-y-2", theme.bgSecondary, theme.borderPrimary)}>
          {checklist.length > 0 ? checklist.map((item) => (
            <div key={item.id} className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={!!item.checked} readOnly disabled className="mt-0.5" />
              <span className={cn(item.checked && "line-through opacity-70")}>{item.text}</span>
            </div>
          )) : (
            <div className={cn("text-sm", theme.textMuted)}>{t('No checklist items')}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className={cn("flex items-center gap-2 text-sm font-medium", theme.textSecondary)}>
        <CheckSquare size={16} />
        {t('Checklist')}
      </label>
      <ChecklistComponent
        checklist={checklist}
        onChange={onChecklistChange}
        className="border rounded-lg p-3"
      />
    </div>
  );
};
